import React, { useState, useEffect } from "react";
import { ReactComponent as FeedbackIcon } from "../../assets/icons/svg/feedback_icon.svg";
import { BASE_URL } from "../../api/urls";
import { DEFAULT_FONT_FAMILY } from "../../config/fontConfig";

interface FeedbackCardProps {
  appName: string;
  feedbackCount: number;
  daysAgo: string;
  status: "Invited" | "Viewed" | "In progress" | "Done" | "Shared with me";
  imageUrl: string;
  onClick?: () => void;
  onStatusChange?: (newStatus: "Invited" | "Viewed" | "In progress") => void;
  onDelete?: () => void;
  isNew?: boolean;
  cardId?: number;
  preloadedDevelopers?: Developer[];
  workspaceOwnerName?: string;
  workspaceOwnerProfile?: string | null;
}

interface Developer {
  email: string;
  name: string | null;
  profile_image: string | null;
}

interface User {
  email: string;
  name: string | null;
  profile_image: string | null;
  role: string;
  isOwner: boolean;
}

// Helper to get full image URL
const getFullImageUrl = (imagePath: string | null): string | null => {
  if (!imagePath) return null;
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
    return imagePath;
  }
  const cleanPath = imagePath.replace(/\\/g, "/").replace(/^\/+/, "");
  return `${BASE_URL}/${cleanPath}`;
};

// Helper to get initials
const getInitials = (name: string | null, email: string): string => {
  if (name) {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }
  return email.charAt(0).toUpperCase();
};

const FeedbackCard: React.FC<FeedbackCardProps> = ({
  appName,
  feedbackCount,
  daysAgo,
  status,
  imageUrl,
  onClick,
  onStatusChange,
  onDelete,
  isNew = false,
  cardId,
  preloadedDevelopers,
  workspaceOwnerName,
  workspaceOwnerProfile,
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoadingDevs, setIsLoadingDevs] = useState(!preloadedDevelopers);

  // Combine owner and developers into users array
  useEffect(() => {
    console.log(`🔍 FeedbackCard ${cardId} - Processing user data:`, {
      hasPreloadedDevelopers: !!preloadedDevelopers,
      preloadedCount: preloadedDevelopers?.length || 0,
      hasOwner: !!workspaceOwnerName,
      ownerName: workspaceOwnerName,
      appName: appName,
    });

    const allUsers: User[] = [];

    // Add workspace owner first (if available)
    if (workspaceOwnerName) {
      allUsers.push({
        email: "",
        name: workspaceOwnerName,
        profile_image: workspaceOwnerProfile ?? null, // ✅ Convert undefined to null
        role: "Owner",
        isOwner: true,
      });
    }

    // Add developers
    if (preloadedDevelopers && preloadedDevelopers.length > 0) {
      preloadedDevelopers.forEach((dev) => {
        allUsers.push({
          email: dev.email,
          name: dev.name,
          profile_image: dev.profile_image,
          role: "Developer",
          isOwner: false,
        });
      });
    }

    setUsers(allUsers);
    setIsLoadingDevs(false);

    console.log(
      `✅ FeedbackCard ${cardId} - Using ${allUsers.length} user(s):`,
      allUsers.map((u) => ({ name: u.name, email: u.email, role: u.role })),
    );
  }, [
    cardId,
    preloadedDevelopers,
    workspaceOwnerName,
    workspaceOwnerProfile,
    appName,
  ]);

  const getStatusColor = (
    status: "Invited" | "Viewed" | "In progress" | "Done" | "Shared with me",
  ): string => {
    switch (status) {
      case "Invited":
        return "bg-[#E7F5FF] text-[#1971C2]";
      case "Viewed":
        return "bg-[#FFF4E6] text-[#E8590C]";
      case "In progress":
        return "bg-[#D3F9D8] text-[#2F9E44]";
      case "Done":
        return "bg-[#D3F9D8] text-[#2F9E44]";
      case "Shared with me":
        return "bg-[#FFF4E6] text-[#FF754C]";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  const collapsedOffset = -16;

  return (
    <div
      className="rounded-xl border border-blue-200 overflow-hidden max-w-sm bg-white transition-all cursor-pointer hover:shadow-lg relative flex flex-col"
      onClick={onClick}
    >
      {/* Image Section */}
      <div className="h-44 overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-50">
        <img
          src={imageUrl}
          alt={`${appName} preview`}
          className="w-full h-full object-cover"
        />
      </div>

      {/* Content Section */}
      <div className="p-4 flex flex-col flex-1">
        {/* App Name with User Avatars */}
        <div className="flex flex-col gap-2 mb-2">
          {/* App Name */}
          <div
            className="text-lg font-semibold text-gray-900 truncate"
            title={appName}
            style={{ fontFamily: DEFAULT_FONT_FAMILY }}
          >
            {appName}
          </div>

          {/* User Avatars (Owner + Developers) */}
          {!isLoadingDevs && users.length > 0 && (
            <div
              className="flex items-center"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Count Badge */}
              <div className="relative w-6 h-6">
               <div className="relative z-10 flex items-center justify-center w-6 h-6 rounded-full bg-[#1E1F26] text-white text-[10px] font-semibold border-[2px] border-white">
                  {users.length}
                </div>
              </div>

              {/* User Avatars */}
              {users.map((user, index) => {
                const initials = getInitials(user.name, user.email);
                const fullImageUrl = getFullImageUrl(user.profile_image);

                return (
                  <div
                    key={user.isOwner ? "owner" : user.email}
                    className="relative"
                    style={{
                      marginLeft: `${collapsedOffset}px`,
                      zIndex: users.length - index,
                    }}
                  >
                    <div className="w-6 h-6 rounded-full overflow-hidden flex items-center justify-center border-[2px] border-white">
                      {fullImageUrl ? (
                        <img
                          src={fullImageUrl}
                          alt={user.name || user.email}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div
                          className={`w-full h-full ${
                            user.isOwner
                              ? "bg-gradient-to-br from-blue-400 to-indigo-500"
                              : "bg-gradient-to-br from-purple-400 to-pink-400"
                          } flex items-center justify-center text-white font-semibold text-xs`}
                        >
                          {initials}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Fixed height spacer to match DeveloperFeedbackCard badge container */}
        <div className="h-6 mb-1" />

        {/* Bottom Section */}
        <div className="mt-0">
          <div className="flex justify-between text-sm mb-3">
            <div
              className="flex items-center text-black gap-1"
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            >
              <FeedbackIcon />
              <span>
                {feedbackCount} feedback{feedbackCount !== 1 ? "s" : ""}
              </span>
            </div>
            <div
              className="text-gray-500"
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            >
              {daysAgo}
            </div>
          </div>

          <div className="border-t border-blue-100" />

          <div className="pt-3 flex items-center justify-between">
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                status,
              )}`}
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            >
              {status}
            </span>
            {/* <div className="text-sm font-medium text-blue-600"  style={{ fontFamily: DEFAULT_FONT_FAMILY }}>
              View Feedback
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackCard;
